/**
 * @file hooks/useClientLookups.ts
 * @description 客戶檔案下拉字典的查詢 hook（字典概念重構後）。
 *
 * 字典概念對齊（2026-06-26 重構，見 docs/superpowers/plans/2026-06-26-client-dictionaries-redesign.md）：
 *  - 驗證類型 verification-types：申請類別（初次申請 / 追蹤查驗…），可複選 → clients.verificationType[]
 *  - 驗證資格 verification-qualifications：主體類型（自然人 / 農場…），單選 → clients.verificationQualification
 *  - 驗證類別 verification-categories：產品類別（依方案過濾，含 scheme 欄），可複選 → clients.verificationCategory[]
 *  - 驗證方案 verification-schemes：TOP/TAP/ECO 制度名 + 法規依據（legalBasis），單選顯示用
 *  - 合約來源 contract-sources：不變
 *
 * 兩個典型使用場景：
 *  1. 編輯客戶 Modal 的 Select：使用 activeOnly=true，只顯示啟用項
 *  2. 後台維護頁列表：使用 activeOnly=false，顯示全部（含停用）
 *
 * 字典資料量小（<500 筆），統一以 pageSize=500 一次性拉回，
 * Select 在前端用 antd 內建 showSearch + filterOption 過濾即可。
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * 共用 lookup 項目結構。
 * 重構後部分字典帶額外欄位（皆設為可選，相容只回基礎欄位的字典）：
 *  - scheme：驗證類別（verification-categories）所屬的驗證方案，用於依方案過濾
 *  - code / legalBasis：驗證方案（verification-schemes）的代碼與法規依據
 */
export interface LookupItem {
  id: number;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** 驗證類別專用：所屬驗證方案（TOP/TAP/ECO），null 表示通用 */
  scheme?: string | null;
  /** 驗證方案專用：方案代碼（TOP/TAP/ECO） */
  code?: string | null;
  /** 驗證方案專用：法規依據條文 */
  legalBasis?: string | null;
  /** 有機資材查詢連結專用：連結網址（序4） */
  url?: string | null;
}

/** 後端分頁回應結構 */
export interface LookupListResponse {
  items: LookupItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface LookupQueryOptions {
  /** 是否只查啟用項，預設 false（維護頁要看全部） */
  activeOnly?: boolean;
  /** 是否啟用查詢，預設 true */
  enabled?: boolean;
}

/**
 * 內部共用：依資源名稱建立 useQuery。
 * @param resource 後端 settings 路徑，如 'verification-types'
 */
function useLookup(resource: string, opts: LookupQueryOptions = {}) {
  const { activeOnly = false, enabled = true } = opts;
  return useQuery({
    queryKey: ['settings-lookup', resource, { activeOnly }],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('pageSize', '500');
      if (activeOnly) params.set('activeOnly', 'true');
      return apiClient.get<LookupListResponse>(`/api/settings/${resource}?${params.toString()}`);
    },
    enabled,
    // 字典變動頻率低，5 分鐘內走快取避免重複請求
    staleTime: 5 * 60 * 1000,
  });
}

/** 驗證類型字典（申請類別：初次申請 / 追蹤查驗…，對應 clients.verificationType[]，可複選） */
export function useVerificationTypes(opts?: LookupQueryOptions) {
  return useLookup('verification-types', opts);
}

/** 驗證資格字典（主體類型：自然人 / 農場 / …，對應 clients.verificationQualification，單選） */
export function useVerificationQualifications(opts?: LookupQueryOptions) {
  return useLookup('verification-qualifications', opts);
}

/** 驗證類別字典（產品類別，含 scheme 欄，對應 clients.verificationCategory[]，可複選且依方案過濾） */
export function useVerificationCategories(opts?: LookupQueryOptions) {
  return useLookup('verification-categories', opts);
}

/** 驗證方案字典（TOP/TAP/ECO 制度名 + 法規依據，單選顯示用） */
export function useVerificationSchemes(opts?: LookupQueryOptions) {
  return useLookup('verification-schemes', opts);
}

/** 合約來源字典 */
export function useContractSources(opts?: LookupQueryOptions) {
  return useLookup('contract-sources', opts);
}

/** 部門字典（員工所屬部門/組別） */
export function useDepartments(opts?: LookupQueryOptions) {
  return useLookup('departments', opts);
}

/** 職稱字典（job-titles，員工職稱如「稽核員」） */
export function useJobTitles(opts?: LookupQueryOptions) {
  return useLookup('job-titles', opts);
}

/** 聘用職務字典（job-functions，如「技術評審」） */
export function useJobFunctions(opts?: LookupQueryOptions) {
  return useLookup('job-functions', opts);
}
